import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RequestDemoButton } from 'components';
import { abaSchedulingFeatures, abaSchedulingFaqs } from './constants';
import { landingImages, Svg } from 'assets';
import { animationParams, animationVariants } from 'fragments';
import { IsImageLoaded } from 'utils';
import { IntegrationsSection, INTEGRATIONS_THEMES, ReadyTransform, FaqSection } from '../../common';

export const AbaSchedulingFragment = () => {
    const location = useLocation();
    const imageLoaded = IsImageLoaded(landingImages.abaSchedulingHeroImg);

    useEffect(() => {
        if (location?.hash) {
            const timer = setTimeout(() => {
                const el = document.querySelector(location.hash);
                el?.scrollIntoView({ behavior: 'smooth' });
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [location]);

    return (
        <div className="clinical-page-wrapper">
            {/* Hero Section */}
            <section
                className="clinical-hero"
                style={{ background: 'linear-gradient(135deg, #E7F0FC 0%, #F5F9FE 30%, #EFF6FF 100%)' }}
            >
                <div className="container">
                    <div className="hero-content">
                        <div className="hero-text-block" style={{ flexGrow: 1.4, maxWidth: '100%' }}>
                            <span className="hero-badge" style={{ color: '#07387D' }}>ABA SCHEDULING. LESS BACK-AND-FORTH.</span>
                            <div style={{ overflow: 'hidden' }}>
                                <motion.h1
                                    className="hero-title"
                                    style={{ color: '#0948A1' }}
                                    {...animationParams}
                                    variants={animationVariants.textVariant}
                                >
                                    ABA Scheduling Software
                                    for Faster, Cleaner <span style={{ color: '#3D85E9' }}>Appointment Planning</span>
                                </motion.h1>
                            </div>

                            <motion.div
                                {...animationParams}
                                variants={animationVariants.staggerContainerVariant}
                            >
                                <motion.p
                                    className="hero-subtitle"
                                    variants={animationVariants.leftToRightVariant}
                                >
                                    Build schedules around staff availability, client needs, authorizations, recurring sessions, and operational guardrails.
                                    TherapyLake helps ABA teams reduce scheduling mistakes and keep appointments connected to daily practice workflows.
                                </motion.p>
                            </motion.div>

                            <div className="hero-btn-container hero-btn-container-practice">
                                <RequestDemoButton arrowIcon />
                            </div>
                        </div>
                        <div className="hero-image-block">
                            <div className="hero-image-placeholder" style={{ maxWidth: '533px', aspectRatio: '533/514' }}>
                                <motion.img
                                    src={landingImages.abaSchedulingHeroImgSmall}
                                    {...animationParams}
                                    variants={animationVariants.cardsVariant}
                                    alt="banner"
                                />

                                <motion.img
                                    src={landingImages.abaSchedulingHeroImg}
                                    alt="banner"
                                    style={{
                                        opacity: imageLoaded ? 1 : 0,
                                        transition: 'opacity 0.5s ease-in-out',
                                        visibility: imageLoaded ? 'visible' : 'hidden',
                                        zIndex: imageLoaded ? 1 : 0,
                                    }}
                                    {...animationParams}
                                    variants={animationVariants.cardsVariant}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Alternating Features Section */}
            <section className="clinical-features">
                <div className="container">
                    {abaSchedulingFeatures.map(({ BadgeIcon, ...feature }, index) => {
                        const isEven = index % 2 === 0;
                        const featureImageLoaded = IsImageLoaded(feature?.image);

                        return (
                            <div
                                key={feature?.id}
                                id={feature?.id}
                                className={`feature-row ${isEven ? 'row-even' : 'row-odd'}`}
                            >
                                <div className="feature-content">
                                    {BadgeIcon &&
                                        <div className="feature-badge__icon" style={{ background: '#E7F0FC' }}>
                                            <BadgeIcon
                                                style={{
                                                    filter: 'brightness(0) saturate(100%) invert(24%) sepia(100%) saturate(4629%) hue-rotate(209deg) brightness(91%) contrast(91%)',
                                                }}
                                            />
                                        </div>
                                    }
                                    <div style={{ overflow: 'hidden' }}>
                                        <motion.p
                                            className="feature-badge__label"
                                            style={{ color: '#0948A1' }}
                                            {...animationParams}
                                            variants={animationVariants.titleVariantMobile}
                                        >
                                            {feature?.badgeLabel}
                                        </motion.p>
                                    </div>

                                    <div style={{ overflow: 'hidden' }}>
                                        <motion.h2
                                            className="feature-title"
                                            {...animationParams}
                                            variants={animationVariants.titleVariantMobile}
                                        >
                                            {feature.title}
                                        </motion.h2>
                                    </div>

                                    <div style={{ overflow: 'hidden' }}>
                                        <motion.p
                                            className="feature-text"
                                            {...animationParams}
                                            variants={animationVariants.titleVariant}
                                        >
                                            {feature.text}
                                        </motion.p>
                                    </div>

                                    <motion.ul
                                        className="feature-bullets"
                                        {...animationParams}
                                        variants={animationVariants.staggerContainerVariant}
                                    >
                                        {feature.bullets.map((bullet, idx) => (
                                            <motion.li
                                                key={idx}
                                                variants={animationVariants.leftToRightVariant}
                                            >
                                                <span className="bullet-icon bullet-icon-practice">
                                                    <Svg.Check2Svg />
                                                </span>
                                                <span className="bullet-text">{bullet}</span>
                                            </motion.li>
                                        ))}
                                    </motion.ul>
                                </div>
                                <div className="feature-image" style={{ borderColor: '#EFF6FF' }}>
                                    <motion.img
                                        src={feature?.imageSmall}
                                        {...animationParams}
                                        variants={animationVariants.cardsVariant}
                                        alt="banner"
                                    />

                                    <motion.img
                                        src={feature?.image}
                                        alt="banner"
                                        style={{
                                            opacity: featureImageLoaded ? 1 : 0,
                                            transition: 'opacity 0.5s ease-in-out',
                                            visibility: featureImageLoaded ? 'visible' : 'hidden',
                                            zIndex: featureImageLoaded ? 1 : 0,
                                        }}
                                        {...animationParams}
                                        variants={animationVariants.cardsVariant}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <IntegrationsSection
                themeConfig={INTEGRATIONS_THEMES.PracticeManagement}
                classNameWrapper="practice-management-integrations-wrapper"
            />

            <FaqSection faqs={abaSchedulingFaqs} />

            <ReadyTransform
                className="ready-transform-practice"
                title="Ready to Simplify ABA Scheduling?"
                description="Join ABA practices using TherapyLake to streamline billing workflows, improve revenue visibility, and manage daily operations from one connected platform. Start your free trial today!"
            />
        </div>
    );
};
